// The variant matrix: one row per BOM line, one column per variant, each cell the quantity that variant
// gets (and the part, where an option picks it). Click a cell (or move to it and type) to change just that
// variant: "2" sets the quantity, "0" leaves it out, "1 GCU2-SHELL-BK" swaps the part, empty puts it back.
// Keys work like Sell items' edit mode: Enter saves and moves down, Tab across, Esc cancels, arrows move.
import { type KeyboardEvent, useMemo, useRef, useState } from "react";
import { type BomIndex, type BomRow, type FamilySummary, type LineCell, lineCell, variantOptions } from "../../../shared/boms.ts";
import type { Item } from "../../../shared/db.gen.ts";
import { cost2, itemBySku, marginTone, parseQty, pct, useBomPlace } from "../lib/boms.ts";
import { qty } from "../lib/format.ts";
import { mutate } from "../lib/mutate.ts";
import { setSearch } from "../lib/router.tsx";
import { cx } from "../ui/controls.tsx";
import { toast } from "../ui/toast.tsx";

const COL_W = 116;

export function BomMatrix({
  ix,
  bom,
  variants,
  options,
  editable,
  summary,
}: {
  ix: BomIndex;
  bom: BomRow;
  variants: Item[];
  options: { name: string; values: string[] }[];
  editable: boolean;
  summary: FamilySummary;
}) {
  const place = useBomPlace();
  const lines = ix.linesByBom.get(bom.id) ?? [];
  const [cur, setCur] = useState({ r: 0, c: 0 });
  const [edit, setEdit] = useState<{ r: number; c: number; draft: string } | null>(null);
  const grid = useRef<HTMLDivElement>(null);
  // The cell being edited, outside React state: refocusing the grid blurs the input, and that blur
  // must not save the cell a second time.
  const active = useRef<{ r: number; c: number } | null>(null);

  const cells = useMemo(() => lines.map((line) => variants.map((v) => lineCell(ix, line, v.id, variantOptions(v)))), [ix, lines, variants]);
  const costs = useMemo(() => new Map(summary.variants.map((v) => [v.variant.id, v])), [summary]);

  // Header rows: the first option's values span their variants; the rest of each title below.
  const first = options[0];
  const groups = useMemo(() => {
    if (!first || options.length < 2) return null;
    const out: { label: string; span: number }[] = [];
    for (const v of variants) {
      const label = variantOptions(v)[first.name] ?? "";
      if (out.at(-1)?.label === label) out[out.length - 1].span++;
      else out.push({ label, span: 1 });
    }
    return out;
  }, [variants, options, first]);
  const columnLabel = (v: Item) => {
    const opts = variantOptions(v);
    const rest = (groups ? options.slice(1) : options).map((o) => opts[o.name]).filter(Boolean);
    return rest.join(" / ") || v.shopify_variant_title || v.sku;
  };

  const sku = (id: string | null) => (id ? (ix.itemById.get(id)?.sku ?? "?") : "");
  const textOf = (cell: LineCell) =>
    cell.componentId !== cell.baseComponentId && cell.componentId ? `${cell.qty} ${sku(cell.componentId)}` : String(cell.qty);

  const save = (r: number, c: number, text: string) => {
    const line = lines[r];
    const variant = variants[c];
    const cell = cells[r]?.[c];
    if (!line || !variant || !cell) return;
    const existing = ix.overridesByLine.get(line.id)?.get(variant.id);
    const t = text.trim();
    if (t === textOf(cell)) return;
    if (t === "") {
      if (existing) mutate("bom_override.remove", { id: existing.id });
      return;
    }
    const m = t.match(/^([\d.,]*)\s*(?:[x×*]\s+)?(.*)$/i);
    const qtyText = m?.[1] ?? "";
    const skuText = (m?.[2] ?? "").trim();
    let n = qtyText ? parseQty(qtyText) : undefined;
    if (qtyText && n === undefined) return toast.error("Type a quantity, optionally followed by a SKU");
    let component = cell.componentId;
    if (skuText) {
      const item = itemBySku(skuText);
      if (!item) return toast.error(`No part with SKU ${skuText}`);
      component = item.id;
      // A part typed into an empty cell comes in at the line's quantity.
      n ??= cell.qty || Number(line.qty) || 1;
    }
    n ??= cell.qty;
    // Store only what differs from what the line gives this variant anyway.
    const patch = { qty: n === cell.baseQty ? null : n, component_item_id: component === cell.baseComponentId ? null : component };
    if (patch.qty == null && patch.component_item_id == null) {
      if (existing) mutate("bom_override.remove", { id: existing.id });
    } else if (existing) mutate("bom_override.update", { id: existing.id, patch });
    else mutate("bom_override.create", { id: crypto.randomUUID(), bom_line_id: line.id, variant_item_id: variant.id, ...patch });
  };

  const move = (r: number, c: number) => {
    const next = { r: Math.max(0, Math.min(lines.length - 1, r)), c: Math.max(0, Math.min(variants.length - 1, c)) };
    setCur(next);
    grid.current?.querySelector(`[data-cell="${next.r}:${next.c}"]`)?.scrollIntoView({ block: "nearest", inline: "nearest" });
  };
  const start = (r: number, c: number, draft?: string) => {
    if (!editable || !lines[r] || !variants[c]) return;
    setCur({ r, c });
    active.current = { r, c };
    setEdit({ r, c, draft: draft ?? textOf(cells[r][c]) });
  };
  const finish = (text: string | null, then?: { dr: number; dc: number }) => {
    const at = active.current;
    if (!at) return;
    active.current = null;
    setEdit(null);
    if (text !== null) save(at.r, at.c, text);
    if (then) move(at.r + then.dr, at.c + then.dc);
    grid.current?.focus();
  };

  const onKey = (e: KeyboardEvent) => {
    if (active.current) return; // the cell's input handles its own keys (even before it has mounted)
    const { r, c } = cur;
    if (e.key === "ArrowDown") move(r + 1, c);
    else if (e.key === "ArrowUp") move(r - 1, c);
    else if (e.key === "ArrowRight" || (e.key === "Tab" && !e.shiftKey)) move(r, c + 1);
    else if (e.key === "ArrowLeft" || (e.key === "Tab" && e.shiftKey)) move(r, c - 1);
    else if (e.key === "Enter" || e.key === "F2") start(r, c);
    else if ((e.key === "Delete" || e.key === "Backspace") && editable) save(r, c, "");
    else if (editable && e.key.length === 1 && /[\d.a-z]/i.test(e.key) && !e.ctrlKey && !e.metaKey) start(r, c, e.key);
    else return;
    e.preventDefault();
  };

  if (!lines.length) return <div className="p-6 text-center text-muted">No lines yet. Add parts on the Lines tab.</div>;
  if (!variants.length) return <div className="p-6 text-center text-muted">This product has no live variants in HHL Ops.</div>;

  const sticky = "sticky left-0 z-10 bg-surface";
  const head = "h-8 border-b border-line bg-surface-2 px-2 text-[11px] font-medium tracking-wide text-muted uppercase";
  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* biome-ignore lint/a11y/useSemanticElements: a keyboard grid over a table, like DataTable */}
      <div ref={grid} role="grid" tabIndex={0} onKeyDown={onKey} className="relative min-h-0 flex-1 overflow-auto focus:outline-none">
        <table className="border-separate border-spacing-0 text-[13px]" style={{ minWidth: 340 + variants.length * COL_W }}>
          <thead className="sticky top-0 z-20">
            {groups && (
              <tr>
                <th className={cx(head, sticky, "z-30 bg-surface-2")} colSpan={2} />
                {groups.map((g) => (
                  <th key={g.label} colSpan={g.span} className={cx(head, "border-l text-center text-text")}>
                    <span className="normal-case">{g.label}</span>
                  </th>
                ))}
              </tr>
            )}
            <tr>
              <th className={cx(head, sticky, "z-30 w-[340px] min-w-[340px] bg-surface-2 text-left")} colSpan={2}>
                Line
              </th>
              {variants.map((v) => (
                <th
                  key={v.id}
                  title={`${v.shopify_variant_title} · ${v.sku}`}
                  className={cx(head, "cursor-pointer border-l text-center font-normal hover:text-text")}
                  style={{ width: COL_W, minWidth: COL_W, maxWidth: COL_W }}
                  onClick={() => setSearch({ [place.tab]: "costs", variant: v.id })}
                >
                  <div className="truncate normal-case">{columnLabel(v)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lines.map((line, r) => {
              const part = ix.itemById.get(line.component_item_id ?? "");
              const what = line.option_name ? `${line.ref_designator ? `${line.ref_designator} ` : ""}by ${line.option_name}` : (part?.name ?? "");
              return (
                <tr key={line.id}>
                  <td className={cx(sticky, "h-[34px] w-10 border-b border-line/60 pr-2 text-right text-faint")}>{line.position}</td>
                  <td
                    className={cx(sticky, "left-10 h-[34px] w-[300px] max-w-[300px] border-b border-line/60 pr-2")}
                    onClick={() => setSearch({ [place.tab]: null, line: line.id })}
                    title="Open the line"
                  >
                    <div className="flex min-w-0 cursor-pointer items-center gap-2">
                      <span className="w-32 shrink-0 truncate font-mono text-[12px]">{part?.sku ?? ""}</span>
                      <span className="min-w-0 truncate">{what}</span>
                      <span className="ml-auto shrink-0 text-[12px] text-faint">× {qty(line.qty)}</span>
                    </div>
                  </td>
                  {variants.map((v, c) => {
                    const cell = cells[r][c];
                    const isCur = cur.r === r && cur.c === c;
                    const editing = edit?.r === r && edit.c === c;
                    const showPart = (line.option_name || cell.componentId !== line.component_item_id) && cell.qty > 0;
                    const missing = cell.qty > 0 && !cell.componentId;
                    return (
                      <td
                        key={v.id}
                        data-cell={`${r}:${c}`}
                        onMouseDown={() => setCur({ r, c })}
                        onClick={() => start(r, c)}
                        title={
                          cell.overridden
                            ? `Changed for this variant (the line gives ${qty(cell.baseQty)}${cell.baseComponentId ? ` × ${sku(cell.baseComponentId)}` : ""})`
                            : missing
                              ? `No part for ${line.option_name} = ${cell.optionValue}`
                              : undefined
                        }
                        className={cx(
                          "h-[34px] border-b border-l border-line/60 px-1.5 text-center tabular-nums",
                          editable && "cursor-text",
                          cell.overridden && "bg-accent-soft/70",
                          isCur && "outline-2 -outline-offset-2 outline-accent",
                        )}
                        style={{ width: COL_W, minWidth: COL_W, maxWidth: COL_W }}
                      >
                        {editing ? (
                          <input
                            autoFocus
                            defaultValue={edit.draft}
                            onFocus={(e) => {
                              // A cell started by typing keeps the typed character; Enter or a click selects all.
                              if (edit.draft.length > 1 || textOf(cell) === edit.draft) e.currentTarget.select();
                              else e.currentTarget.setSelectionRange(edit.draft.length, edit.draft.length);
                            }}
                            onBlur={(e) => finish(e.currentTarget.value)}
                            onKeyDown={(e) => {
                              const value = e.currentTarget.value;
                              if (e.key === "Enter") finish(value, { dr: e.shiftKey ? -1 : 1, dc: 0 });
                              else if (e.key === "Tab") finish(value, { dr: 0, dc: e.shiftKey ? -1 : 1 });
                              else if (e.key === "Escape") finish(null);
                              else return;
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            className="h-7 w-full rounded border border-accent bg-surface px-1.5 text-center text-[13px] focus:outline-none"
                          />
                        ) : cell.qty > 0 ? (
                          <div className="flex min-w-0 flex-col items-center leading-tight">
                            <span className={cx(missing && "text-danger")}>{missing ? `${qty(cell.qty)} ?` : qty(cell.qty)}</span>
                            {showPart && <span className="max-w-full truncate font-mono text-[10px] text-muted">{sku(cell.componentId)}</span>}
                          </div>
                        ) : (
                          <span className="text-faint">–</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          <tfoot className="sticky bottom-0 z-20">
            <tr>
              <td className={cx(sticky, "h-8 border-t border-line bg-surface-2 text-right text-[11px] text-muted uppercase")} colSpan={2}>
                <span className="pr-2">Cost</span>
              </td>
              {variants.map((v) => {
                const s = costs.get(v.id);
                return (
                  <td key={v.id} className="h-8 border-t border-l border-line bg-surface-2 text-center font-mono text-[12px]">
                    {s ? cost2(s.resolved.cost.total) : ""}
                    {s && !s.resolved.cost.complete && (
                      <span className="text-warn" title={`${s.resolved.cost.missing} without a cost`}>
                        +
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
            <tr>
              <td className={cx(sticky, "h-8 bg-surface-2 text-right text-[11px] text-muted uppercase")} colSpan={2}>
                <span className="pr-2">Margin</span>
              </td>
              {variants.map((v) => {
                const s = costs.get(v.id);
                return (
                  <td key={v.id} className={cx("h-8 border-l border-line bg-surface-2 text-center font-mono text-[12px]", marginTone(s?.margin))}>
                    {s?.margin == null ? "—" : pct(s.margin)}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="flex h-7 shrink-0 items-center gap-3 border-t border-line px-3 text-[11px] text-faint">
        {editable ? (
          <span>
            Click a cell or type to change one variant · "2" quantity · "0" leaves it out · "1 SKU" swaps the part · Delete puts it back · Enter moves down ·
            Tab across
          </span>
        ) : (
          <span>Shaded cells are changed for that variant. Start a new revision to change them.</span>
        )}
      </div>
    </div>
  );
}
